import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  PieChart as RePieChart,
  Pie,
  Cell,
} from "recharts";
import {
  ArrowLeft,
  Equal,
  Loader2,
  Save,
  ImageIcon,
  Plus,
  Minus,
  History,
  Warehouse,
  Tag,
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  Filter,
  Eye,
  Check,
  ChevronsUpDown,
  Sparkles,
  X,
} from "lucide-react";

const vatRates = [
  { value: "0", label: "%0" },
  { value: "1", label: "%1" },
  { value: "8", label: "%8" },
  { value: "10", label: "%10" },
  { value: "18", label: "%18" },
  { value: "20", label: "%20" },
];

const providerOrder = ["parasut", "bizimhesap", "benimpos", "woocommerce", "trendyol", "hepsiburada", "amazon", "n11", "pazarama", "idefix", "shopify", "ikas", "shopier"];
const providerLabel = {
  parasut: "Paraşüt",
  bizimhesap: "BizimHesap",
  benimpos: "BenimPOS",
  woocommerce: "WooCommerce",
  trendyol: "Trendyol",
  hepsiburada: "Hepsiburada",
  amazon: "Amazon",
  n11: "N11",
  pazarama: "Pazarama",
  idefix: "idefix",
  shopify: "Shopify",
  ikas: "ikas",
  shopier: "Shopier",
};
const providerLogo = {
  parasut: "/integrations/parasut.svg",
  bizimhesap: "/integrations/bizimhesap.svg",
  benimpos: "/integrations/benimpos.png?v=20261030",
  woocommerce: "/integrations/woocommerce.svg",
  trendyol: "/integrations/trendyol.svg",
  hepsiburada: "/integrations/hepsiburada.svg",
  amazon: "/integrations/amazon.svg",
  n11: "/integrations/n11.png",
  pazarama: "/integrations/pazarama.svg",
  idefix: "/integrations/idefix.svg",
  shopify: "/integrations/shopify.svg",
  ikas: "/integrations/ikas.svg",
  shopier: "/integrations/shopier.svg",
};
const panelCard = "surface-card";
const panelHeader = "text-[15px] font-bold tracking-tight text-foreground [font-family:Manrope,sans-serif]";
const tableHeader = "grid grid-cols-12 gap-2 bg-muted/50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const accountingProviderLabel = {
  parasut: "Paraşüt",
  bizimhesap: "BizimHesap",
  benimpos: "BenimPOS",
};

function parseHash(hash) {
  const h = String(hash || "").replace(/^#/, "");
  if (!h) return { section: "information", provider: null };
  if (h.startsWith("integrations-")) return { section: "integrations", provider: h.replace("integrations-", "") || null };
  if (["information", "images", "job-history", "stock-history", "sales-reports"].includes(h)) return { section: h, provider: null };
  return { section: "information", provider: null };
}

function setHash(section, provider = null) {
  const next = section === "integrations" && provider ? `integrations-${provider}` : section;
  if (window.location.hash !== `#${next}`) {
    window.history.replaceState(null, "", `${window.location.pathname}#${next}`);
  }
}

function ProviderMark({ provider, ok }) {
  const src = providerLogo[provider];
  if (src) {
    return (
      <span className={cn("flex h-5 w-5 items-center justify-center rounded-md border bg-card", ok ? "border-emerald-300" : "border-border")}> 
        <img src={src} alt={provider} className={cn("h-3.5 w-3.5 object-contain", !ok && "grayscale opacity-60")} />
      </span>
    );
  }
  return <span className={cn("flex h-5 w-5 items-center justify-center rounded-md border text-[10px] font-semibold", ok ? "border-emerald-300 text-emerald-700" : "border-border text-muted-foreground")}>{provider.slice(0, 2).toUpperCase()}</span>;
}

function EditorToolbar({ onCmd }) {
  const TBtn = ({ cmd, children }) => (
    <button
      type="button"
      className="rounded border border-border/70 px-2 py-1 text-xs hover:bg-muted"
      onMouseDown={(e) => {
        e.preventDefault();
        onCmd(cmd);
      }}
    >
      {children}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-1 rounded-t-md border border-b-0 border-border/70 bg-muted/30 p-2">
      <TBtn cmd="bold"><Bold className="h-3.5 w-3.5" /></TBtn>
      <TBtn cmd="italic"><Italic className="h-3.5 w-3.5" /></TBtn>
      <TBtn cmd="insertUnorderedList"><List className="h-3.5 w-3.5" /></TBtn>
      <TBtn cmd="insertOrderedList"><ListOrdered className="h-3.5 w-3.5" /></TBtn>
      <TBtn cmd="undo"><Undo2 className="h-3.5 w-3.5" /></TBtn>
      <TBtn cmd="redo"><Redo2 className="h-3.5 w-3.5" /></TBtn>
    </div>
  );
}

function SearchablePicker({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  onSearch,
  loading = false,
  disabled = false,
  itemLabel = (x) => String(x?.name || ""),
  itemValue = (x) => String(x?.id || ""),
  itemSearch = null,
}) {
  const [open, setOpen] = useState(false);
  const selected = (options || []).find((o) => itemValue(o) === String(value || ""));
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!open) return;
    if (typeof onSearch !== "function") return;
    const timer = setTimeout(() => {
      onSearch(searchText);
    }, 250);
    return () => clearTimeout(timer);
  }, [open, searchText, onSearch]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="flex h-10 w-full items-center justify-between rounded-md border border-border bg-card px-3 text-left text-sm disabled:cursor-not-allowed disabled:bg-muted/50"
        >
          <span className={selected ? "text-foreground truncate" : "text-muted-foreground truncate"}>
            {selected ? itemLabel(selected) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={typeof onSearch !== "function"}>
          <CommandInput
            value={searchText}
            onValueChange={setSearchText}
            placeholder={searchPlaceholder || `${placeholder} ara...`}
          />
          <CommandList>
            <CommandEmpty>{loading ? "Yükleniyor..." : "Sonuç bulunamadı."}</CommandEmpty>
            {(options || []).map((opt) => {
              const iv = itemValue(opt);
              const label = itemLabel(opt);
              const searchVal = itemSearch ? itemSearch(opt) : `${label} ${iv}`;
              return (
                <CommandItem
                  key={iv || label}
                  value={searchVal}
                  onSelect={() => {
                    onChange(iv);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", String(value || "") === iv ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{label}</span>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [accountingProvider, setAccountingProvider] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [view, setView] = useState(parseHash(window.location.hash));
  const [selectedImage, setSelectedImage] = useState("");

  const [rangeFilter, setRangeFilter] = useState("month");
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [integrationOps, setIntegrationOps] = useState([]);
  const [timelineMovements, setTimelineMovements] = useState([]);
  const [sales, setSales] = useState({ total_revenue: 0, total_quantity: 0, total_orders: 0, by_provider: [] });

  const [productSettings, setProductSettings] = useState({
    manage_stock_from_accounting: false,
    selected_warehouse_id: null,
    selected_warehouse_name: null,
  });

  const [stockSheetOpen, setStockSheetOpen] = useState(false);
  const [stockMode, setStockMode] = useState("add");
  const [stockAmount, setStockAmount] = useState("");
  const [stockReason, setStockReason] = useState("");
  const [stockUpdating, setStockUpdating] = useState(false);
  const [stockMovements, setStockMovements] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [n11CreateOpen, setN11CreateOpen] = useState(false);
  const [n11MatchOpen, setN11MatchOpen] = useState(false);
  const [n11Loading, setN11Loading] = useState(false);
  const [n11Saving, setN11Saving] = useState(false);
  const [n11SourceProducts, setN11SourceProducts] = useState([]);
  const [n11Search, setN11Search] = useState("");
  const [n11SelectedExternalId, setN11SelectedExternalId] = useState("");
  const [n11Categories, setN11Categories] = useState([]);
  const [n11CategoryLoading, setN11CategoryLoading] = useState(false);
  const [n11SelectedCategoryKey, setN11SelectedCategoryKey] = useState("");
  const [sourceMatchOpen, setSourceMatchOpen] = useState(false);
  const [sourceMatchLoading, setSourceMatchLoading] = useState(false);
  const [sourceMatchSaving, setSourceMatchSaving] = useState(false);
  const [sourceMatchUnlinking, setSourceMatchUnlinking] = useState(false);
  const [sourceMatchSearch, setSourceMatchSearch] = useState("");
  const [sourceMatchItems, setSourceMatchItems] = useState([]);
  const [sourceMatchSelectedExternalId, setSourceMatchSelectedExternalId] = useState("");
  const [sourceCreateOpen, setSourceCreateOpen] = useState(false);
  const [sourceCreating, setSourceCreating] = useState(false);
  const [sourceCreateTemplateExternalId, setSourceCreateTemplateExternalId] = useState("");
  const [sourceCreateResult, setSourceCreateResult] = useState(null);
  const [trendyolBrands, setTrendyolBrands] = useState([]);
  const [trendyolCategories, setTrendyolCategories] = useState([]);
  const [trendyolAttributes, setTrendyolAttributes] = useState([]);
  const [trendyolAddresses, setTrendyolAddresses] = useState([]);
  const [trendyolCargoCompanies, setTrendyolCargoCompanies] = useState([]);
  const [trendyolMetaLoading, setTrendyolMetaLoading] = useState(false);
  const [trendyolBrandLoading, setTrendyolBrandLoading] = useState(false);
  const [trendyolCategoryLoading, setTrendyolCategoryLoading] = useState(false);
  const [trendyolAddressLoading, setTrendyolAddressLoading] = useState(false);
  const [sourceCreateForm, setSourceCreateForm] = useState({
    product_name: "",
    stock_code: "",
    barcode: "",
    sale_price: "",
    list_price: "",
    quantity: "",
    category_id: "",
    brand_id: "",
    cargo_company_id: "",
    shipment_address_id: "",
    returning_address_id: "",
    dimensional_weight: "1",
    delivery_duration: "1",
    attributes: [],
  });
  const [aiEnriching, setAiEnriching] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [imageSearching, setImageSearching] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [manualImageUrl, setManualImageUrl] = useState("");
  const [manualImageSaving, setManualImageSaving] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [tabUrlInput, setTabUrlInput] = useState("");
  const [categories, setCategories] = useState([]);
  const [parasutMatchOpen, setParasutMatchOpen] = useState(false);
  const [parasutLoading, setParasutLoading] = useState(false);
  const [parasutSaving, setParasutSaving] = useState(false);
  const [parasutUnlinking, setParasutUnlinking] = useState(false);
  const [parasutSearch, setParasutSearch] = useState("");
  const [parasutItems, setParasutItems] = useState([]);
  const [parasutSelectedId, setParasutSelectedId] = useState("");
  const [parasutSelectedName, setParasutSelectedName] = useState("");

  const [form, setForm] = useState({
    name: "", sku: "", barcode: "", price: "", regular_price: "",
    ean: "", brand: "", cost_price: "", weight: "", category: "", description: "",
    vat_rate: "1", category_id: "", subcategory_id: "",
  });

  const fetchProduct = useCallback(async () => {
    try {
      const data = await api.get(`/products/${id}`);
      setProduct(data);
      setSelectedImage(data.image_url || (data.images || [])[0] || "");
      setForm({
        name: data.name || "",
        sku: data.sku || "",
        barcode: data.barcode || "",
        ean: data.ean || "",
        brand: data.brand || data.manufacturer || data.vendor || "",
        price: data.price?.toString() || "",
        regular_price: data.regular_price?.toString() || "",
        cost_price: data.cost_price?.toString() || "",
        weight: data.weight || "",
        category: data.category || "",
        category_id: data.category_id || "",
        subcategory_id: data.subcategory_id || "",
        description: data.description || "",
        vat_rate: data.vat_rate?.toString() || "1",
      });
    } catch {
      toast.error("Ürün bulunamadı");
      navigate("/panel/urunler");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchIntegrations = useCallback(async () => {
    try {
      const rows = await api.get("/integrations");
      setIntegrations(Array.isArray(rows) ? rows : []);
    } catch {
      setIntegrations([]);
    }
  }, []);

  const fetchAccountingProvider = useCallback(async () => {
    try {
      const cfg = await api.get("/settings/accounting");
      const p = String(cfg?.provider || "").toLowerCase();
      setAccountingProvider(["bizimhesap", "parasut", "benimpos"].includes(p) ? p : "");
    } catch {
      setAccountingProvider("");
    }
  }, []);

  const fetchInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const res = await api.get(`/products/${id}/insights?range=${encodeURIComponent(rangeFilter)}`);
      setIntegrationOps(res?.integration_operations || []);
      setTimelineMovements(res?.stock_movements || []);
      setSales(res?.sales || { total_revenue: 0, total_quantity: 0, total_orders: 0, by_provider: [] });
    } catch {
      setIntegrationOps([]);
      setTimelineMovements([]);
      setSales({ total_revenue: 0, total_quantity: 0, total_orders: 0, by_provider: [] });
    } finally {
      setInsightsLoading(false);
    }
  }, [id, rangeFilter]);

  const fetchStockMovements = useCallback(async () => {
    try {
      const data = await api.get(`/stock/movements?product_id=${id}`);
      setStockMovements(data);
    } catch {
      setStockMovements([]);
    }
  }, [id]);

  const fetchProductSettings = useCallback(async () => {
    try {
      const data = await api.get("/settings/product-settings");
      setProductSettings({
        manage_stock_from_accounting: Boolean(
          data?.manage_stock_from_accounting ?? data?.manage_stock_from_parasut
        ),
        selected_warehouse_id: data?.selected_warehouse_id || null,
        selected_warehouse_name: data?.selected_warehouse_name || null,
      });
    } catch {
      setProductSettings({
        manage_stock_from_accounting: false,
        selected_warehouse_id: null,
        selected_warehouse_name: null,
      });
    }
  }, []);

  useEffect(() => {
    const onHash = () => setView(parseHash(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    fetchProduct();
    fetchIntegrations();
    fetchAccountingProvider();
    fetchProductSettings();
    api.get("/categories").then(setCategories).catch(() => {});
  }, [fetchProduct, fetchIntegrations, fetchAccountingProvider, fetchProductSettings]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const integrationRows = useMemo(() => {
    const byProvider = new Map((integrations || []).map((i) => [String(i.provider || "").toLowerCase(), i]));
    const links = product?.channel_links || {};
    const activeAccountingProvider =
      ["bizimhesap", "parasut", "benimpos"].includes(accountingProvider)
        ? accountingProvider
        : (links?.bizimhesap ? "bizimhesap" : "parasut");
    const orderedProviders = [activeAccountingProvider, ...providerOrder.filter((p) => p !== activeAccountingProvider)];
    return orderedProviders
      .map((provider) => {
        const integration = byProvider.get(provider);
        const isAccountingProvider = provider === "parasut" || provider === "bizimhesap" || provider === "benimpos";
        const matched = provider === "parasut"
          ? Boolean(product?.parasut_product_id || links?.parasut)
          : Boolean(links?.[provider]);
        const effectiveIntegration = integration || (isAccountingProvider && provider === activeAccountingProvider
          ? { provider, name: providerLabel[provider] || provider, integration_no: null }
          : null);
        return {
          provider,
          integration: effectiveIntegration,
          label: providerLabel[provider] || provider,
          matched,
          channelDetail: ((product?.channel_details || {})[provider] || {}),
          externalId: provider === "parasut"
            ? (product?.parasut_product_id || links?.parasut || "")
            : (links?.[provider] || ""),
        };
      })
      .filter((x) => x.integration || x.matched || x.provider === activeAccountingProvider);
  }, [integrations, product, accountingProvider]);

  const selectedIntegrationRow = useMemo(() => {
    if (view.section !== "integrations") return null;
    const provider = view.provider || integrationRows[0]?.provider || "parasut";
    return integrationRows.find((r) => r.provider === provider) || null;
  }, [view, integrationRows]);

  const isN11IntegrationView = view.section === "integrations" && selectedIntegrationRow?.provider === "n11";
  const n11IntegrationId = String(selectedIntegrationRow?.integration?.id || "").trim();
  const isParasutIntegrationView = view.section === "integrations" && selectedIntegrationRow?.provider === "parasut";
  const isSourceMatchIntegrationView = view.section === "integrations"
    && selectedIntegrationRow?.provider
    && !["parasut", "n11"].includes(String(selectedIntegrationRow.provider));
  const sourceMatchIntegrationId = String(selectedIntegrationRow?.integration?.id || "").trim();
  const parasutWarehouseId = String(
    selectedIntegrationRow?.integration?.credentials?.invoice_settings?.selected_warehouse_id
    || productSettings?.selected_warehouse_id
    || ""
  ).trim();
  const n11RecommendedProduct = useMemo(() => {
    if (!Array.isArray(n11SourceProducts) || !n11SourceProducts.length) return null;
    const sku = String(product?.sku || "").trim().toLowerCase();
    const barcode = String(product?.barcode || "").trim().toLowerCase();
    const name = String(product?.name || "").trim().toLowerCase();
    return n11SourceProducts.find((x) => {
      const xSku = String(x?.sku || "").trim().toLowerCase();
      const xBarcode = String(x?.barcode || "").trim().toLowerCase();
      const xName = String(x?.name || "").trim().toLowerCase();
      return (sku && xSku && sku === xSku)
        || (barcode && xBarcode && barcode === xBarcode)
        || (name && xName && name === xName);
    }) || null;
  }, [n11SourceProducts, product?.sku, product?.barcode, product?.name]);

  useEffect(() => {
    if (view.section !== "integrations") return;
    if (!integrationRows.length) return;
    const hasCurrent = view.provider && integrationRows.some((r) => r.provider === view.provider);
    if (hasCurrent) return;
    const nextProvider = integrationRows[0]?.provider;
    if (!nextProvider) return;
    setView({ section: "integrations", provider: nextProvider });
    setHash("integrations", nextProvider);
  }, [view.section, view.provider, integrationRows]);

  const fetchN11SourceProducts = useCallback(async (query = "") => {
    if (!n11IntegrationId) return;
    setN11Loading(true);
    try {
      const res = await api.get(
        `/integrations/${n11IntegrationId}/source-products?search=${encodeURIComponent(query || "")}&page=1&per_page=100`
      );
      const rows = Array.isArray(res?.items) ? res.items : [];
      setN11SourceProducts(rows);
    } catch (error) {
      toast.error(error.message || "N11 mağaza ürünleri alınamadı");
      setN11SourceProducts([]);
    } finally {
      setN11Loading(false);
    }
  }, [n11IntegrationId]);

  const fetchN11Categories = useCallback(async (query = "") => {
    if (!n11IntegrationId) return;
    setN11CategoryLoading(true);
    try {
      const res = await api.get(
        `/n11/${n11IntegrationId}/categories?search=${encodeURIComponent(query || "")}&page=1&per_page=250`
      );
      setN11Categories(Array.isArray(res?.items) ? res.items : []);
    } catch (error) {
      toast.error(error.message || "N11 kategorileri alınamadı");
      setN11Categories([]);
    } finally {
      setN11CategoryLoading(false);
    }
  }, [n11IntegrationId]);

  const handleN11SaveMatch = useCallback(async () => {
    if (!n11SelectedExternalId) {
      toast.error("Mağaza ürünü seçin");
      return;
    }
    const selectedCategory = n11Categories.find(
      (c) => `${c?.category_id || ""}|${c?.category_name || ""}` === n11SelectedCategoryKey
    ) || {};
    const categoryId = String(selectedCategory?.category_id || "").trim();
    const categoryName = String(selectedCategory?.category_name || "").trim();
    const categoryPath = String(selectedCategory?.category_path || "").trim();
    if (!categoryId && !categoryName) {
      toast.error("N11 kategori seçimi zorunlu");
      return;
    }
    setN11Saving(true);
    try {
      await api.post(`/products/${id}/link-channel`, {
        provider: "n11",
        external_id: n11SelectedExternalId,
        source_product_id: n11SelectedExternalId,
        category_id: categoryId,
        category_name: categoryName,
        category_path: categoryPath,
      });
      toast.success("N11 eşleştirmesi kaydedildi");
      await fetchProduct();
    } catch (error) {
      toast.error(error.message || "N11 eşleştirmesi kaydedilemedi");
    } finally {
      setN11Saving(false);
    }
  }, [n11SelectedExternalId, n11Categories, n11SelectedCategoryKey, id, fetchProduct]);

  useEffect(() => {
    if (!isN11IntegrationView || !n11IntegrationId) return;
    fetchN11SourceProducts("");
    fetchN11Categories("");
    const existingExternalId = String(selectedIntegrationRow?.externalId || "").trim();
    setN11SelectedExternalId(existingExternalId);
    const detail = selectedIntegrationRow?.channelDetail || {};
    setN11SelectedCategoryKey(`${detail?.category_id || ""}|${detail?.category_name || ""}`);
  }, [isN11IntegrationView, n11IntegrationId, selectedIntegrationRow, fetchN11SourceProducts, fetchN11Categories]);

  const fetchParasutProducts = useCallback(async (query = "") => {
    setParasutLoading(true);
    try {
      const warehousePart = parasutWarehouseId ? `&warehouse_id=${encodeURIComponent(parasutWarehouseId)}` : "";
      const res = await api.get(`/parasut/products?search=${encodeURIComponent(query || "")}&page=1&per_page=25${warehousePart}`);
      const rows = Array.isArray(res?.items) ? res.items : [];
      setParasutItems(rows);
    } catch (error) {
      toast.error(error.message || "Paraşüt ürünleri alınamadı");
      setParasutItems([]);
    } finally {
      setParasutLoading(false);
    }
  }, [parasutWarehouseId]);

  const handleParasutSaveMatch = useCallback(async () => {
    if (!parasutSelectedId) {
      toast.error("Paraşüt ürünü seçin");
      return;
    }
    setParasutSaving(true);
    try {
      await api.post(
        `/products/${id}/link-parasut?parasut_product_id=${encodeURIComponent(parasutSelectedId)}&parasut_product_name=${encodeURIComponent(parasutSelectedName || "")}`
      );
      toast.success("Paraşüt eşleştirmesi kaydedildi");
      await fetchProduct();
    } catch (error) {
      toast.error(error.message || "Paraşüt eşleştirmesi kaydedilemedi");
    } finally {
      setParasutSaving(false);
    }
  }, [parasutSelectedId, parasutSelectedName, id, fetchProduct]);

  const handleParasutUnlink = useCallback(async () => {
    setParasutUnlinking(true);
    try {
      await api.post(`/products/${id}/unlink-parasut`);
      toast.success("Paraşüt eşleştirmesi kaldırıldı");
      await fetchProduct();
    } catch (error) {
      toast.error(error.message || "Paraşüt eşleştirmesi kaldırılamadı");
    } finally {
      setParasutUnlinking(false);
    }
  }, [id, fetchProduct]);

  useEffect(() => {
    if (!isParasutIntegrationView) return;
    const externalId = String(selectedIntegrationRow?.externalId || "").trim();
    const externalName = String(product?.parasut_product_name || "").trim();
    setParasutSelectedId(externalId);
    setParasutSelectedName(externalName);
    setParasutSearch("");
    setParasutItems([]);
    setParasutMatchOpen(false);
  }, [isParasutIntegrationView, selectedIntegrationRow?.externalId, product?.parasut_product_name]);

  useEffect(() => {
    if (!isParasutIntegrationView || !parasutMatchOpen) return;
    fetchParasutProducts("");
  }, [isParasutIntegrationView, parasutMatchOpen, fetchParasutProducts]);

  const fetchSourceMatchProducts = useCallback(async (query = "") => {
    if (!sourceMatchIntegrationId) return;
    setSourceMatchLoading(true);
    try {
      const res = await api.get(
        `/integrations/${sourceMatchIntegrationId}/source-products?search=${encodeURIComponent(query || "")}&page=1&per_page=50`
      );
      const rows = Array.isArray(res?.items) ? res.items : [];
      setSourceMatchItems(rows);
    } catch (error) {
      toast.error(error.message || "Entegrasyon ürünleri alınamadı");
      setSourceMatchItems([]);
    } finally {
      setSourceMatchLoading(false);
    }
  }, [sourceMatchIntegrationId]);

  const fetchTrendyolBrands = useCallback(async (query = "") => {
    if (!sourceMatchIntegrationId) return;
    setTrendyolBrandLoading(true);
    try {
      const res = await api.get(`/trendyol/${sourceMatchIntegrationId}/brands?search=${encodeURIComponent(query || "")}&page=1&per_page=100`);
      setTrendyolBrands(Array.isArray(res?.items) ? res.items : []);
    } catch (error) {
      setTrendyolBrands([]);
      if (query) toast.error(error.message || "Trendyol markaları alınamadı");
    } finally {
      setTrendyolBrandLoading(false);
    }
  }, [sourceMatchIntegrationId]);

  const fetchTrendyolCategories = useCallback(async (query = "") => {
    if (!sourceMatchIntegrationId) return;
    setTrendyolCategoryLoading(true);
    try {
      const res = await api.get(`/trendyol/${sourceMatchIntegrationId}/categories?search=${encodeURIComponent(query || "")}&page=1&per_page=200`);
      setTrendyolCategories(Array.isArray(res?.items) ? res.items : []);
    } catch (error) {
      setTrendyolCategories([]);
      if (query) toast.error(error.message || "Trendyol kategorileri alınamadı");
    } finally {
      setTrendyolCategoryLoading(false);
    }
  }, [sourceMatchIntegrationId]);

  const fetchTrendyolAddresses = useCallback(async () => {
    if (!sourceMatchIntegrationId) return;
    setTrendyolAddressLoading(true);
    try {
      const res = await api.get(`/trendyol/${sourceMatchIntegrationId}/addresses`);
      const addr = Array.isArray(res?.items) ? res.items : [];
      const shipment = addr.find((a) => {
        const t = String(a?.address_type || "").toLowerCase();
        return t.includes("shipment") || t.includes("sevk");
      });
      const returning = addr.find((a) => {
        const t = String(a?.address_type || "").toLowerCase();
        return t.includes("return") || t.includes("iade");
      });
      setTrendyolAddresses(addr);
      setSourceCreateForm((prev) => ({
        ...prev,
        shipment_address_id: prev.shipment_address_id || String(shipment?.id || addr?.[0]?.id || ""),
        returning_address_id: prev.returning_address_id || String(returning?.id || addr?.[0]?.id || ""),
      }));
    } catch (error) {
      setTrendyolAddresses([]);
      toast.error(error.message || "Trendyol depoları alınamadı");
    } finally {
      setTrendyolAddressLoading(false);
    }
  }, [sourceMatchIntegrationId]);

  const fetchTrendyolMeta = useCallback(async () => {
    if (!sourceMatchIntegrationId) return;
    setTrendyolMetaLoading(true);
    try {
      const [cargoRes] = await Promise.allSettled([
        api.get(`/trendyol/${sourceMatchIntegrationId}/cargo-companies`),
        fetchTrendyolBrands(""),
        fetchTrendyolCategories(""),
        fetchTrendyolAddresses(),
      ]);
      const cargoPayload = cargoRes.status === "fulfilled" ? cargoRes.value : {};
      const cargoItems = Array.isArray(cargoPayload?.items) ? cargoPayload.items : [];
      setTrendyolCargoCompanies(cargoItems);
      if (cargoItems.length) {
        const valid = new Set(cargoItems.map((c) => String(c?.id || "")));
        setSourceCreateForm((prev) => {
          const current = String(prev.cargo_company_id || "");
          const next = valid.has(current) ? current : String(cargoItems[0]?.id || "");
          return { ...prev, cargo_company_id: next };
        });
      }
    } finally {
      setTrendyolMetaLoading(false);
    }
  }, [sourceMatchIntegrationId, fetchTrendyolBrands, fetchTrendyolCategories, fetchTrendyolAddresses]);

  const fetchTrendyolCategoryAttributes = useCallback(async (categoryId) => {
    const cid = String(categoryId || "").trim();
    if (!sourceMatchIntegrationId || !cid) {
      setTrendyolAttributes([]);
      return;
    }
    setTrendyolMetaLoading(true);
    try {
      const res = await api.get(`/trendyol/${sourceMatchIntegrationId}/category-attributes?category_id=${encodeURIComponent(cid)}`);
      const attrs = Array.isArray(res?.items) ? res.items : [];
      setTrendyolAttributes(attrs);
      setSourceCreateForm((prev) => ({
        ...prev,
        attributes: attrs
          .filter((a) => a?.required)
          .map((a) => ({
            attributeId: a.id,
            attributeValueId: a?.values?.[0]?.id || "",
            customAttributeValue: "",
          })),
      }));
    } catch (error) {
      toast.error(error.message || "Kategori özellikleri alınamadı");
      setTrendyolAttributes([]);
    } finally {
      setTrendyolMetaLoading(false);
    }
  }, [sourceMatchIntegrationId]);

  const handleSourceMatchSave = useCallback(async () => {
    const provider = String(selectedIntegrationRow?.provider || "").toLowerCase();
    if (!provider) {
      toast.error("Entegrasyon sağlayıcısı bulunamadı");
      return;
    }
    if (!sourceMatchSelectedExternalId) {
      toast.error("Mağaza ürünü seçin");
      return;
    }
    setSourceMatchSaving(true);
    try {
      await api.post(`/products/${id}/link-channel`, {
        provider,
        external_id: sourceMatchSelectedExternalId,
        source_product_id: sourceMatchSelectedExternalId,
      });
      toast.success(`${providerLabel[provider] || provider} eşleştirmesi kaydedildi`);
      await fetchProduct();
    } catch (error) {
      toast.error(error.message || "Eşleştirme kaydedilemedi");
    } finally {
      setSourceMatchSaving(false);
    }
  }, [selectedIntegrationRow?.provider, sourceMatchSelectedExternalId, id, fetchProduct]);

  const handleSourceMatchUnlink = useCallback(async () => {
    const provider = String(selectedIntegrationRow?.provider || "").toLowerCase();
    if (!provider) return;
    setSourceMatchUnlinking(true);
    try {
      await api.post(`/products/${id}/unlink-channel`, { provider });
      toast.success(`${providerLabel[provider] || provider} eşleştirmesi kaldırıldı`);
      await fetchProduct();
    } catch (error) {
      toast.error(error.message || "Eşleştirme kaldırılamadı");
    } finally {
      setSourceMatchUnlinking(false);
    }
  }, [selectedIntegrationRow?.provider, id, fetchProduct]);

  useEffect(() => {
    if (!isSourceMatchIntegrationView) return;
    setSourceMatchOpen(false);
    setSourceCreateOpen(false);
    setSourceMatchSearch("");
    setSourceMatchItems([]);
    setSourceMatchSelectedExternalId(String(selectedIntegrationRow?.externalId || "").trim());
    setSourceCreateTemplateExternalId("");
    setSourceCreateResult(null);
    setSourceCreateForm({
      product_name: String(product?.name || ""),
      stock_code: String(product?.sku || ""),
      barcode: String(product?.barcode || ""),
      sale_price: String(product?.price ?? ""),
      list_price: String(product?.regular_price ?? product?.price ?? ""),
      quantity: String(product?.stock ?? ""),
      category_id: "",
      brand_id: "",
      cargo_company_id: "",
      shipment_address_id: "",
      returning_address_id: "",
      dimensional_weight: "1",
      delivery_duration: "1",
      attributes: [],
    });
  }, [
    isSourceMatchIntegrationView,
    selectedIntegrationRow?.externalId,
    selectedIntegrationRow?.provider,
    product?.name,
    product?.sku,
    product?.barcode,
    product?.price,
    product?.regular_price,
    product?.stock,
    product?.description,
    product?.image_url,
  ]);

  useEffect(() => {
    if (!isSourceMatchIntegrationView || !sourceMatchOpen) return;
    fetchSourceMatchProducts("");
  }, [isSourceMatchIntegrationView, sourceMatchOpen, fetchSourceMatchProducts]);

  useEffect(() => {
    if (!isSourceMatchIntegrationView || !sourceCreateOpen) return;
    fetchSourceMatchProducts("");
  }, [isSourceMatchIntegrationView, sourceCreateOpen, fetchSourceMatchProducts]);

  useEffect(() => {
    const provider = String(selectedIntegrationRow?.provider || "").toLowerCase();
    if (!isSourceMatchIntegrationView || !sourceCreateOpen || provider !== "trendyol") return;
    fetchTrendyolMeta();
  }, [isSourceMatchIntegrationView, sourceCreateOpen, selectedIntegrationRow?.provider, fetchTrendyolMeta]);

  useEffect(() => {
    const provider = String(selectedIntegrationRow?.provider || "").toLowerCase();
    if (!sourceCreateOpen || provider !== "trendyol") return;
    fetchTrendyolCategoryAttributes(sourceCreateForm.category_id);
  }, [sourceCreateForm.category_id, sourceCreateOpen, selectedIntegrationRow?.provider, fetchTrendyolCategoryAttributes]);

  const handleSourceCreateFromTemplate = useCallback(async () => {
    const provider = String(selectedIntegrationRow?.provider || "").toLowerCase();
    if (!["trendyol", "hepsiburada"].includes(provider)) {
      toast.error("Bu platformda yeni ürün oluşturma desteklenmiyor");
      return;
    }
    if (provider === "trendyol") {
      if (!String(sourceCreateForm.brand_id || "").trim()) {
        toast.error("Trendyol için marka seçimi zorunlu");
        return;
      }
      if (!String(sourceCreateForm.category_id || "").trim()) {
        toast.error("Trendyol için kategori seçimi zorunlu");
        return;
      }
    }
    const isTrendyolManual = provider === "trendyol";
    if (!sourceCreateTemplateExternalId && !isTrendyolManual) {
      toast.error("Template ürün seçin");
      return;
    }
    setSourceCreating(true);
    setSourceCreateResult(null);
    try {
      const res = await api.post(`/products/${id}/create-channel-product`, {
        provider,
        template_external_id: sourceCreateTemplateExternalId || "",
        integration_id: sourceMatchIntegrationId || undefined,
        product_name: String(sourceCreateForm.product_name || "").trim() || undefined,
        stock_code: String(sourceCreateForm.stock_code || "").trim() || undefined,
        barcode: String(sourceCreateForm.barcode || "").trim() || undefined,
        title: String(sourceCreateForm.product_name || "").trim() || undefined,
        category_id: sourceCreateForm.category_id ? Number(sourceCreateForm.category_id) : undefined,
        brand_id: sourceCreateForm.brand_id ? Number(sourceCreateForm.brand_id) : undefined,
        cargo_company_id: String(sourceCreateForm.cargo_company_id || "").trim() || undefined,
        shipment_address_id: sourceCreateForm.shipment_address_id ? Number(sourceCreateForm.shipment_address_id) : undefined,
        returning_address_id: sourceCreateForm.returning_address_id ? Number(sourceCreateForm.returning_address_id) : undefined,
        dimensional_weight: sourceCreateForm.dimensional_weight !== "" ? Number(sourceCreateForm.dimensional_weight) : undefined,
        delivery_duration: sourceCreateForm.delivery_duration !== "" ? Number(sourceCreateForm.delivery_duration) : undefined,
        attributes: Array.isArray(sourceCreateForm.attributes) ? sourceCreateForm.attributes : [],
        sale_price: sourceCreateForm.sale_price !== "" ? Number(sourceCreateForm.sale_price) : undefined,
        list_price: sourceCreateForm.list_price !== "" ? Number(sourceCreateForm.list_price) : undefined,
        quantity: sourceCreateForm.quantity !== "" ? Number(sourceCreateForm.quantity) : undefined,
      });
      setSourceCreateResult({
        ok: true,
        message: String(res?.message || "Ürün oluşturuldu ve eşleştirildi"),
        provider,
        external_id: String(res?.external_id || ""),
        template_external_id: String(res?.template_external_id || sourceCreateTemplateExternalId),
      });
      toast.success(`${selectedIntegrationRow?.label || provider} üzerinde yeni ürün oluşturuldu`);
      await fetchProduct();
    } catch (error) {
      setSourceCreateResult({
        ok: false,
        message: String(error?.message || "Yeni ürün oluşturma başarısız"),
        provider,
        external_id: "",
        template_external_id: String(sourceCreateTemplateExternalId),
      });
      toast.error(error.message || "Yeni ürün oluşturma başarısız");
    } finally {
      setSourceCreating(false);
    }
  }, [
    selectedIntegrationRow?.provider,
    selectedIntegrationRow?.label,
    sourceCreateTemplateExternalId,
    sourceCreateForm,
    id,
    sourceMatchIntegrationId,
    fetchProduct,
  ]);

  const stockLevel = product?.stock || 0;
  const activeAccountingProvider = ["bizimhesap", "parasut", "benimpos"].includes(accountingProvider)
    ? accountingProvider
    : "parasut";
  const effectiveStockLevel = stockLevel;
  const stockStatus = effectiveStockLevel === 0 ? "Yetersiz" : effectiveStockLevel < 10 ? "Düşük" : "Yeterli";
  const stockBg = effectiveStockLevel === 0 ? "bg-muted/50 border-border" : effectiveStockLevel < 10 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200";

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/products/${id}`, {
        name: form.name,
        sku: form.sku,
        barcode: form.barcode,
        ean: form.ean,
        brand: form.brand,
        manufacturer: form.brand,
        price: parseFloat(form.price) || 0,
        regular_price: parseFloat(form.regular_price) || 0,
        cost_price: form.cost_price ? parseFloat(form.cost_price) : null,
        weight: form.weight,
        category: form.category,
        category_id: form.category_id || null,
        subcategory_id: form.subcategory_id || null,
        description: form.description,
        vat_rate: parseFloat(form.vat_rate) || 1,
      });
      toast.success("Ürün kaydedildi");
      await fetchProduct();
    } catch (error) {
      toast.error(error.message || "Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  const handleAiEnrich = async (applyDirectly = false) => {
    setAiEnriching(true);
    setAiPreview(null);
    try {
      const result = await api.post("/ai/products/enrich", {
        product_id: id,
        apply: applyDirectly,
      });
      if (applyDirectly) {
        toast.success("AI zenginleştirme uygulandı");
        await fetchProduct();
        setForm((prev) => ({
          ...prev,
          description: result.result?.description || prev.description,
        }));
      } else {
        setAiPreview(result.result);
        toast.success("AI içerik oluşturuldu — önizleyin ve uygulayın");
      }
    } catch (err) {
      toast.error(err.message || "AI zenginleştirme başarısız");
    } finally {
      setAiEnriching(false);
    }
  };

  const handleImageSearch = async () => {
    setImageSearching(true);
    setImagePreview(null);
    try {
      const result = await api.post("/ai/products/images", {
        product_id: id,
        apply: false,
      });
      setImagePreview(result);
      if (result.images?.length) {
        toast.success(`${result.images.length} ürün fotoğrafı bulundu`);
      } else {
        toast.warning("Doğrulanmış ürün fotoğrafı bulunamadı");
      }
    } catch (err) {
      toast.error(err.message || "Fotoğraf araması başarısız");
    } finally {
      setImageSearching(false);
    }
  };

  const applyAiPreview = async () => {
    if (!aiPreview) return;
    setAiEnriching(true);
    try {
      // Mevcut önizleme verisini direkt kaydet (AI'ı yeniden çalıştırma)
      const patch = {};
      if (aiPreview.description) patch.description = aiPreview.description;
      if (aiPreview.short_description) patch.short_description = aiPreview.short_description;
      if (aiPreview.seo_title) patch.seo_title = aiPreview.seo_title;
      if (aiPreview.seo_description) patch.seo_description = aiPreview.seo_description;
      if (aiPreview.technical_specs) patch.technical_specs = aiPreview.technical_specs;
      if (aiPreview.brand_suggestion && !form.brand) {
        patch.brand = aiPreview.brand_suggestion;
        patch.manufacturer = aiPreview.brand_suggestion;
      }
      if (aiPreview.ai_confidence_score != null) patch.ai_confidence_score = aiPreview.ai_confidence_score;
      patch.ai_enrichment_status = "done";

      // Kategori otomatik atama (ürünün mevcut category_id yoksa)
      if (aiPreview.suggested_parent_category && !form.category_id) {
        try {
          const { category_id, subcategory_id } = await api.post("/categories/resolve-or-create", {
            parent_name: aiPreview.suggested_parent_category,
            sub_name: aiPreview.suggested_sub_category || "",
          });
          if (category_id) patch.category_id = category_id;
          if (subcategory_id) patch.subcategory_id = subcategory_id;
        } catch {}
      }

      await api.patch(`/products/${id}`, patch);
      toast.success("AI içerik ürüne uygulandı");
      setAiPreview(null);
      await Promise.all([fetchProduct(), api.get("/categories").then(setCategories).catch(() => {})]);
      setForm((prev) => ({
        ...prev,
        description: aiPreview.description || prev.description,
        brand: (aiPreview.brand_suggestion && !prev.brand) ? aiPreview.brand_suggestion : prev.brand,
      }));
    } catch (err) {
      toast.error(err.message || "Uygulama hatası");
    } finally {
      setAiEnriching(false);
    }
  };

  const handleBulkSalesAction = async (action) => {
    setBulkProcessing(true);
    try {
      const res = await api.post(`/products/${id}/bulk-sales-status`, { action });
      toast.success(
        action === "open"
          ? `Tüm entegrasyonlarda satışa açıldı (${res.affected_count || 0})`
          : `Tüm entegrasyonlarda satışa kapatıldı (${res.affected_count || 0})`
      );
      await fetchProduct();
      await fetchInsights();
    } catch (error) {
      toast.error(error.message || "Toplu işlem başarısız");
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleStockUpdate = async () => {
    if (!stockAmount) {
      toast.error("Miktar girin");
      return;
    }
    setStockUpdating(true);
    try {
      const qty = parseInt(stockAmount, 10);
      const safeQty = Math.abs(qty);
      const typeMap = { add: "in", subtract: "out", adjust: "adjustment" };
      const requestQty = stockMode === "adjust" ? Number.isFinite(qty) ? qty : 0 : safeQty;
      const res = await api.post("/stock/adjust", {
        product_id: id,
        quantity: requestQty,
        movement_type: typeMap[stockMode],
        reason: stockReason || "Manuel güncelleme",
        sync_woocommerce: false,
        sync_all: false,
        sync_targets:
          activeAccountingProvider === "parasut"
            ? ["parasut"]
            : activeAccountingProvider === "bizimhesap"
              ? ["bizimhesap"]
              : activeAccountingProvider === "benimpos"
                ? ["benimpos"]
                : [],
      });
      toast.success("Stok güncellendi");
      const skipped = res?.skipped_providers || {};
      const skippedKeys = Object.keys(skipped);
      if (skippedKeys.length) {
        const parasutError = skipped?.parasut ? ` Paraşüt: ${String(skipped.parasut)}` : "";
        toast.info(`Bazı platformlar güncellenemedi: ${skippedKeys.map((x) => providerLabel[x] || x).join(", ")}.${parasutError}`);
      }
      setStockAmount("");
      setStockReason("");
      setStockMode("add");
      await fetchProduct();
      await fetchStockMovements();
      await fetchInsights();
    } catch (error) {
      toast.error(error.message || "Stok güncelleme hatası");
    } finally {
      setStockUpdating(false);
    }
  };

  const openStockSheet = () => {
    setStockMode("add");
    setStockSheetOpen(true);
    fetchStockMovements();
  };

  const onEditorCmd = (cmd) => document.execCommand(cmd, false);

  const selectMainSection = (section) => {
    setView({ section, provider: null });
    setHash(section);
  };

  const selectIntegrationSection = (provider) => {
    setView({ section: "integrations", provider });
    setHash("integrations", provider);
  };

  if (loading) {
    return <div className="flex min-h-[400px] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!product) return null;
  const chartRows = (sales.by_provider || []).map((row) => ({
    name: row.integration_name || row.provider_label || row.provider,
    shortName: (row.integration_name || row.provider_label || row.provider || "").slice(0, 10),
    revenue: Number(row.revenue || 0),
    orders: Number(row.orders || 0),
  }));
  const pieColors = ["#0ea5e9", "#22c55e", "#f59e0b", "#8b5cf6", "#14b8a6", "#f97316", "#64748b", "#06b6d4", "#a3a3a3"];

  return (
    <div className="page-shell space-y-4 pb-4" data-testid="product-detail-page">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/panel/urunler")}
            className="h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted/50 inline-flex items-center justify-center transition-colors shrink-0"
          >
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          <div className="app-page-header">
            <h1 className="app-page-title">{product.name || "Ürün Detayı"}</h1>
            {(product.sku || product.barcode) && (
              <p className="app-page-subtitle">{product.sku}{product.barcode ? ` · ${product.barcode}` : ""}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-9">
          <div className={panelCard + " overflow-hidden"}>
            {view.section !== "information" && (
              <div className="sheet-heading">
                <h2>
                  {view.section === "images" ? "Ürün Resimleri"
                   : view.section === "integrations" ? "Entegrasyon Ürünleri"
                   : view.section === "job-history" ? "Entegrasyon İşlemleri"
                   : view.section === "stock-history" ? "Stok Hareketleri"
                   : view.section === "sales-reports" ? "Satış Raporları"
                   : "Ürün Detayı"}
                </h2>
              </div>
            )}
            {view.section === "information" && (
              <div className="sheet-heading">
                <h2>Genel Bilgiler</h2>
                <div className="sheet-heading-actions">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={bulkProcessing}>
                        {bulkProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Toplu İşlem
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => handleBulkSalesAction("open")}>
                        Tüm entegrasyonlarda satışa aç
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleBulkSalesAction("close")}>
                        Tüm entegrasyonlarda satışa kapat
                      </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
            )}
            <div className="p-3 sm:p-5">
              {view.section === "information" && (
                <div className="space-y-4">

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Ürün Adı</Label>
                      <Input className="h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Stok Kodu</Label>
                      <Input className="h-11" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Barkod</Label>
                      <Input className="h-11" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>EAN</Label>
                      <Input className="h-11" value={form.ean} onChange={(e) => setForm({ ...form, ean: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Marka</Label>
                      <Input className="h-11" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Örn: Pirelli" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Piyasa Satış Fiyatı</Label>
                      <div className="flex gap-2">
                        <Input className="h-11" type="number" value={form.regular_price} onChange={(e) => setForm({ ...form, regular_price: e.target.value })} />
                        <span className="flex items-center rounded-md border bg-muted px-3 text-sm">TRY</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Mağaza Satış Fiyatı</Label>
                      <div className="flex gap-2">
                        <Input className="h-11" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                        <span className="flex items-center rounded-md border bg-muted px-3 text-sm">TRY</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>KDV Oranı</Label>
                      <Select value={form.vat_rate} onValueChange={(v) => setForm({ ...form, vat_rate: v })}>
                        <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {vatRates.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Ağırlık</Label>
                      <div className="flex gap-2">
                        <Input className="h-11" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} />
                        <span className="flex items-center rounded-md border bg-muted px-3 text-sm">kg</span>
                      </div>
                    </div>
                  </div>

                  {/* Kategori seçimi */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Kategori</Label>
                      {categories.length > 0 ? (
                        <Select
                          value={form.category_id || "__none__"}
                          onValueChange={(v) => setForm({ ...form, category_id: v === "__none__" ? "" : v, subcategory_id: "" })}
                        >
                          <SelectTrigger className="h-11"><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Kategori yok —</SelectItem>
                            {categories.filter(c => !c.parent_id).map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                <span className="flex items-center gap-2">
                                  <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || "#6366f1" }} />
                                  {c.name}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          className="h-11"
                          value={form.category}
                          onChange={(e) => setForm({ ...form, category: e.target.value })}
                          placeholder="Kategori (metin)"
                        />
                      )}
                    </div>
                    {categories.length > 0 && form.category_id && categories.filter(c => c.parent_id === form.category_id).length > 0 && (
                      <div className="space-y-1.5">
                        <Label>Alt Kategori</Label>
                        <Select
                          value={form.subcategory_id || "__none__"}
                          onValueChange={(v) => setForm({ ...form, subcategory_id: v === "__none__" ? "" : v })}
                        >
                          <SelectTrigger className="h-11"><SelectValue placeholder="Alt kategori seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Alt kategori yok —</SelectItem>
                            {categories.filter(c => c.parent_id === form.category_id).map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    {/* AI content and image actions are intentionally independent. */}
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium tracking-wide text-muted-foreground">Açıklama Metni</span>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1.5 text-xs border-sky-200 text-sky-700 hover:bg-sky-50 hover:border-sky-300"
                          onClick={handleImageSearch}
                          disabled={imageSearching || aiEnriching}
                        >
                          {imageSearching
                            ? <><Loader2 className="h-3 w-3 animate-spin" /> Aranıyor…</>
                            : <><ImageIcon className="h-3 w-3" /> Fotoğraf Bul</>
                          }
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1.5 text-xs border-violet-200 text-violet-700 hover:bg-violet-50 hover:border-violet-300"
                          onClick={() => handleAiEnrich(false)}
                          disabled={aiEnriching || imageSearching}
                        >
                          {aiEnriching
                            ? <><Loader2 className="h-3 w-3 animate-spin" /> Üretiliyor…</>
                            : <><Sparkles className="h-3 w-3" /> İçeriği Zenginleştir</>
                          }
                        </Button>
                      </div>
                    </div>

                    {imagePreview && (
                      <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4 text-sky-600" />
                            <span className="text-sm font-medium text-sky-900">Ürün Fotoğrafları</span>
                            {imagePreview.source && <span className="text-[10px] text-sky-600">{imagePreview.source}</span>}
                          </div>
                          <button type="button" className="rounded p-1 text-sky-600 hover:bg-sky-100" onClick={() => setImagePreview(null)}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {imagePreview.images?.length ? (
                          <div className="flex gap-3 flex-wrap">
                            {imagePreview.images.map((img, i) => (
                              <div key={img} className="group flex w-28 flex-col gap-1.5">
                                <a href={img} target="_blank" rel="noreferrer" className="block">
                                  <img src={img} alt={`Ürün önerisi ${i + 1}`} className="h-28 w-28 rounded-lg border border-sky-100 bg-white object-contain" />
                                </a>
                                <button
                                  type="button"
                                  className="rounded-md border border-sky-300 bg-white px-2 py-1 text-[11px] font-medium text-sky-700 hover:bg-sky-50"
                                  onClick={async () => {
                                    try {
                                      await api.patch(`/products/${id}`, { image_url: img, images: imagePreview.images });
                                      setProduct(p => ({ ...p, image_url: img, images: imagePreview.images }));
                                      setSelectedImage(img);
                                      toast.success("Fotoğraf ürüne eklendi");
                                    } catch {
                                      toast.error("Fotoğraf eklenemedi");
                                    }
                                  }}
                                >
                                  <Check className="mr-1 inline h-3 w-3" /> Seç ve kaydet
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="rounded-md border border-dashed border-sky-200 bg-white/70 px-4 py-5 space-y-3">
                            <div className="text-center">
                              <p className="text-sm font-medium text-slate-700">Doğrulanmış fotoğraf bulunamadı</p>
                              <p className="mt-1 text-xs text-slate-500">Barkod geçerli değilse tam ürün adı ve model koduyla arama yapılır.</p>
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="url"
                                value={manualImageUrl}
                                onChange={e => setManualImageUrl(e.target.value)}
                                placeholder="Fotoğraf URL'si yapıştırın..."
                                className="flex-1 rounded-md border border-sky-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-sky-400"
                              />
                              <button
                                type="button"
                                disabled={!manualImageUrl.trim() || manualImageSaving}
                                className="rounded-md border border-sky-300 bg-white px-3 py-1.5 text-sm font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-50"
                                onClick={async () => {
                                  const url = manualImageUrl.trim();
                                  if (!url) return;
                                  setManualImageSaving(true);
                                  try {
                                    const newImages = [...(product.images || []).filter(u => u !== url), url];
                                    await api.patch(`/products/${id}`, { image_url: url, images: newImages });
                                    setProduct(p => ({ ...p, image_url: url, images: newImages }));
                                    setSelectedImage(url);
                                    setManualImageUrl("");
                                    toast.success("Fotoğraf ürüne eklendi");
                                  } catch {
                                    toast.error("Fotoğraf eklenemedi");
                                  } finally {
                                    setManualImageSaving(false);
                                  }
                                }}
                              >
                                {manualImageSaving ? "…" : "Ekle"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* AI Preview Panel */}
                    {aiPreview && (
                      <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-violet-600" />
                            <span className="text-sm font-medium text-violet-800">AI Önerisi</span>
                            {aiPreview.ai_confidence_score != null && (
                              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-medium text-violet-600">
                                %{Math.round(aiPreview.ai_confidence_score * 100)} güven
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="rounded p-1 hover:bg-violet-100 text-violet-500"
                            onClick={() => setAiPreview(null)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {aiPreview.short_description && (
                          <div>
                            <p className="text-[11px] font-semibold text-violet-700 mb-1">Kısa Açıklama</p>
                            <p className="text-sm text-gray-700">{aiPreview.short_description}</p>
                          </div>
                        )}
                        {aiPreview.description && (
                          <div>
                            <p className="text-[11px] font-semibold text-violet-700 mb-1">Uzun Açıklama (önizleme)</p>
                            <div
                              className="rounded border border-violet-100 bg-white p-3 text-sm text-gray-700 max-h-40 overflow-y-auto"
                              dangerouslySetInnerHTML={{ __html: aiPreview.description }}
                            />
                          </div>
                        )}
                        {/* Önerilen marka */}
                        {aiPreview.brand_suggestion && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-violet-700">Tespit Edilen Marka:</span>
                            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] text-violet-700">{aiPreview.brand_suggestion}</span>
                            <span className="text-[10px] text-violet-400">(Ürüne Uygula ile kaydedilir)</span>
                          </div>
                        )}

                        {/* Tespit edilen kategori */}
                        {(aiPreview.detected_category || aiPreview.suggested_parent_category) && (
                          <div className="space-y-1">
                            {aiPreview.detected_category && (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-semibold text-violet-700">Tespit Edilen Tür:</span>
                                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] text-violet-700">{aiPreview.detected_category}</span>
                              </div>
                            )}
                            {aiPreview.suggested_parent_category && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[11px] font-semibold text-violet-700">Önerilen Kategori:</span>
                                <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] text-indigo-700 font-medium">
                                  {aiPreview.suggested_parent_category}
                                </span>
                                {aiPreview.suggested_sub_category && (
                                  <>
                                    <span className="text-[10px] text-gray-400">›</span>
                                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-600">
                                      {aiPreview.suggested_sub_category}
                                    </span>
                                  </>
                                )}
                                <span className="text-[10px] text-violet-400">(Ürüne Uygula ile otomatik oluşturulur)</span>
                              </div>
                            )}
                          </div>
                        )}

                        {aiPreview.seo_title && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-[11px] font-semibold text-violet-700 mb-1">SEO Başlık</p>
                              <p className="text-xs text-gray-600 rounded border border-violet-100 bg-white p-2">{aiPreview.seo_title}</p>
                            </div>
                            {aiPreview.seo_description && (
                              <div>
                                <p className="text-[11px] font-semibold text-violet-700 mb-1">Meta Açıklama</p>
                                <p className="text-xs text-gray-600 rounded border border-violet-100 bg-white p-2">{aiPreview.seo_description}</p>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex gap-2 pt-1">
                          <Button
                            type="button"
                            size="sm"
                            className="h-7 text-xs bg-violet-600 hover:bg-violet-700"
                            onClick={applyAiPreview}
                            disabled={aiEnriching}
                          >
                            <Check className="h-3 w-3 mr-1" /> Ürüne Uygula
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => setAiPreview(null)}
                          >
                            Vazgeç
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="relative rounded-md border border-border/70 pt-5">
                      <span className="absolute left-3 top-1.5 text-[11px] font-medium tracking-wide text-muted-foreground">
                        Mevcut Açıklama
                      </span>
                      <EditorToolbar onCmd={onEditorCmd} />
                      <div
                        className="min-h-[230px] rounded-b-md border-t border-border/70 bg-background p-3 text-sm"
                        contentEditable
                        suppressContentEditableWarning
                        onInput={(e) => setForm({ ...form, description: e.currentTarget.innerHTML || "" })}
                        dangerouslySetInnerHTML={{ __html: form.description || "" }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={() => navigate("/panel/urunler")}>Vazgeç</Button>
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Kaydet
                    </Button>
                  </div>
                </div>
              )}

              {view.section === "images" && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-border/80 p-4">
                    {product.images && product.images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                        {product.images.map((img, i) => (
                          <div key={i} className="group rounded-lg border bg-muted/10 p-2">
                            <img src={img} alt="" className="h-24 w-full rounded object-cover" />
                            <div className="mt-1.5 flex items-center justify-between text-muted-foreground">
                              <button
                                type="button"
                                className="rounded p-1 hover:bg-muted"
                                title="Genel bilgilerde göster"
                                onClick={() => {
                                  setSelectedImage(img);
                                  selectMainSection("information");
                                }}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-500"
                                title="Kaldır"
                                onClick={async () => {
                                  try {
                                    const newImages = (product.images || []).filter((_, j) => j !== i);
                                    const newMain = product.image_url === img ? (newImages[0] || "") : product.image_url;
                                    await api.patch(`/products/${id}`, { image_url: newMain, images: newImages });
                                    setProduct(p => ({ ...p, images: newImages, image_url: newMain }));
                                    if (selectedImage === img) setSelectedImage(newMain);
                                    toast.success("Resim kaldırıldı");
                                  } catch {
                                    toast.error("Resim kaldırılamadı");
                                  }
                                }}
                              >×</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Henüz resim yok.</p>
                    )}
                  </div>

                  <div className="rounded-xl border border-border/70 p-4 space-y-3">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowUrlInput(v => !v)}
                    >
                      <div>
                        <p className="font-semibold">URL ile Resim Ekle</p>
                        <p className="text-sm text-muted-foreground">Ürün fotoğrafının URL adresini girerek ekleyin</p>
                      </div>
                      <span className="text-sm font-semibold text-primary">{showUrlInput ? "Kapat" : "Ekle"}</span>
                    </div>
                    {showUrlInput && (
                      <div className="flex gap-2 pt-1">
                        <input
                          type="url"
                          value={tabUrlInput}
                          onChange={e => setTabUrlInput(e.target.value)}
                          placeholder="https://example.com/urun-foto.jpg"
                          className="flex-1 rounded-md border border-border px-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <button
                          type="button"
                          disabled={!tabUrlInput.trim()}
                          className="rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/20 disabled:opacity-50"
                          onClick={async () => {
                            const url = tabUrlInput.trim();
                            if (!url) return;
                            try {
                              const newImages = [...(product.images || []).filter(u => u !== url), url];
                              await api.patch(`/products/${id}`, { image_url: product.image_url || url, images: newImages });
                              setProduct(p => ({ ...p, images: newImages, image_url: p.image_url || url }));
                              setTabUrlInput("");
                              setShowUrlInput(false);
                              toast.success("Resim eklendi");
                            } catch {
                              toast.error("Resim eklenemedi");
                            }
                          }}
                        >
                          Ekle
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {view.section === "integrations" && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-border/80">
                    <div className="flex items-center justify-between border-b bg-muted/50 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ProviderMark provider={selectedIntegrationRow?.provider || integrationRows[0]?.provider || "parasut"} ok={Boolean(selectedIntegrationRow?.matched)} />
                        <p className="text-sm font-semibold">
                          {selectedIntegrationRow?.integration?.integration_no ? `#${selectedIntegrationRow.integration.integration_no} ` : ""}
                          {selectedIntegrationRow?.label || "Entegrasyon"}
                        </p>
                      </div>
                      <Badge variant="outline" className={cn(selectedIntegrationRow?.matched ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-border bg-muted/50 text-foreground/80")}>{selectedIntegrationRow?.matched ? "Başarılı" : "Eşleşmemiş"}</Badge>
                    </div>
                    <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
                      <div className="rounded-lg border bg-muted/20 p-3">
                        <p className="text-xs text-muted-foreground">Seçili Mağaza Ürünü</p>
                        <p className="mt-1 text-sm font-semibold">{product.name}</p>
                      </div>
                      <div className="rounded-lg border bg-muted/20 p-3">
                        <p className="text-xs text-muted-foreground">Stok Adedi</p>
                        <p className="mt-1 text-sm font-semibold tabular-nums">{effectiveStockLevel} Adet</p>
                      </div>
                      <div className="rounded-lg border bg-muted/20 p-3">
                        <p className="text-xs text-muted-foreground">Entegrasyon Kimliği</p>
                        <p className="mt-1 break-all text-sm font-semibold">{selectedIntegrationRow?.externalId || "-"}</p>
                      </div>
                      {selectedIntegrationRow?.provider === "n11" ? (
                        <div className="rounded-lg border bg-muted/20 p-3">
                          <p className="text-xs text-muted-foreground">N11 Kategorisi</p>
                          <p className="mt-1 break-all text-sm font-semibold">
                            {selectedIntegrationRow?.channelDetail?.category_path
                              || selectedIntegrationRow?.channelDetail?.category_name
                              || selectedIntegrationRow?.channelDetail?.category_id
                              || "-"}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {selectedIntegrationRow?.provider === "parasut" ? (
                    <div className="rounded-lg border border-border/80 bg-card">
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2">
                          <ProviderMark provider="parasut" ok={Boolean(selectedIntegrationRow?.externalId)} />
                          <div>
                            <p className="text-base font-semibold">Paraşüt Ürün Eşleştirme</p>
                            <p className="text-xs text-muted-foreground">Paraşüt hesabımdaki mevcut ürün ile eşleştirmek istiyorum</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedIntegrationRow?.externalId ? (
                            <Button variant="outline" size="sm" onClick={handleParasutUnlink} disabled={parasutUnlinking}>
                              {parasutUnlinking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              Kaldır
                            </Button>
                          ) : null}
                          <Button variant="ghost" size="sm" onClick={() => setParasutMatchOpen((v) => !v)}>
                            {parasutMatchOpen ? "Gizle" : "Eşleştir"}
                          </Button>
                        </div>
                      </div>
                      {parasutMatchOpen ? (
                        <div className="space-y-3 border-t px-4 py-3">
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
                            <Input
                              value={parasutSearch}
                              onChange={(e) => setParasutSearch(e.target.value)}
                              placeholder="Paraşüt ürün adı, kod veya barkod ile ara"
                            />
                            <Button
                              variant="outline"
                              onClick={() => fetchParasutProducts(parasutSearch)}
                              disabled={parasutLoading}
                            >
                              {parasutLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ara"}
                            </Button>
                          </div>

                          {parasutWarehouseId ? (
                            <div className="rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                              Depo filtresi aktif: {parasutWarehouseId}
                            </div>
                          ) : null}

                          <div className="space-y-1.5">
                            <Label>Paraşüt Ürünü</Label>
                            <Select
                              value={parasutSelectedId}
                              onValueChange={(val) => {
                                const picked = parasutItems.find((x) => String(x?.id || "") === String(val));
                                setParasutSelectedId(val);
                                setParasutSelectedName(String(picked?.name || ""));
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={parasutLoading ? "Yükleniyor..." : "Ürün seçin"} />
                              </SelectTrigger>
                              <SelectContent>
                                {parasutItems.map((p) => {
                                  const pid = String(p?.id || "").trim();
                                  const label = String(p?.name || pid || "Paraşüt Ürün");
                                  return (
                                    <SelectItem key={pid || label} value={pid}>
                                      {label}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setParasutMatchOpen(false);
                                setParasutSearch("");
                              }}
                            >
                              Vazgeç
                            </Button>
                            <Button onClick={handleParasutSaveMatch} disabled={parasutSaving}>
                              {parasutSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              Kaydet
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {isSourceMatchIntegrationView ? (
                    <>
                      <div className="rounded-lg border border-border/80 bg-card">
                        <div className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ProviderMark provider={selectedIntegrationRow?.provider || "woocommerce"} ok={Boolean(selectedIntegrationRow?.externalId)} />
                            <div>
                              <p className="text-base font-semibold">{selectedIntegrationRow?.label || "Entegrasyon"} Ürün Eşleştirme</p>
                              <p className="text-xs text-muted-foreground">Mağazadaki mevcut ürün ile eşleştirmek istiyorum</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedIntegrationRow?.externalId ? (
                              <Button variant="outline" size="sm" onClick={handleSourceMatchUnlink} disabled={sourceMatchUnlinking}>
                                {sourceMatchUnlinking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Kaldır
                              </Button>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!sourceMatchIntegrationId) {
                                  toast.error("Bu sağlayıcı için aktif entegrasyon bulunamadı");
                                  return;
                                }
                                setSourceMatchOpen((v) => !v);
                              }}
                            >
                              {sourceMatchOpen ? "Gizle" : "Eşleştir"}
                            </Button>
                          </div>
                        </div>
                        {sourceMatchOpen ? (
                          <div className="space-y-3 border-t px-4 py-3">
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
                              <Input
                                value={sourceMatchSearch}
                                onChange={(e) => setSourceMatchSearch(e.target.value)}
                                placeholder="Ürün adı, stok kodu veya barkod ile ara"
                              />
                              <Button
                                variant="outline"
                                onClick={() => fetchSourceMatchProducts(sourceMatchSearch)}
                                disabled={sourceMatchLoading}
                              >
                                {sourceMatchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ara"}
                              </Button>
                            </div>

                            <div className="space-y-1.5">
                              <Label>Mağaza Ürünü</Label>
                              <Select
                                value={sourceMatchSelectedExternalId}
                                onValueChange={setSourceMatchSelectedExternalId}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={sourceMatchLoading ? "Yükleniyor..." : "Ürün seçin"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {sourceMatchItems.map((p) => {
                                    const ext = String(p?.external_id || p?.id || "").trim();
                                    const label = String(p?.name || ext || "Ürün");
                                    return (
                                      <SelectItem key={ext || label} value={ext}>
                                        {label}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setSourceMatchOpen(false);
                                  setSourceMatchSearch("");
                                }}
                              >
                                Vazgeç
                              </Button>
                              <Button onClick={handleSourceMatchSave} disabled={sourceMatchSaving}>
                                {sourceMatchSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Kaydet
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {["trendyol", "hepsiburada"].includes(String(selectedIntegrationRow?.provider || "").toLowerCase()) ? (
                        <div className="rounded-lg border border-border/80 bg-card">
                          <div className="flex items-center justify-between px-4 py-3">
                            <div className="flex items-center gap-2">
                              <ProviderMark provider={selectedIntegrationRow?.provider || "trendyol"} ok={Boolean(selectedIntegrationRow?.externalId)} />
                              <div>
                                <p className="text-base font-semibold">Yeni Ürün Olarak Ekle</p>
                                <p className="text-xs text-muted-foreground">
                                  {String(selectedIntegrationRow?.provider || "").toLowerCase() === "trendyol"
                                    ? "Kategori/marka/özellik alanlarını doldurarak Trendyol'da yeni kayıt açmak istiyorum"
                                    : "Template ürün bilgilerini kullanarak pazaryerinde yeni kayıt açmak istiyorum"}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!sourceMatchIntegrationId) {
                                  toast.error("Bu sağlayıcı için aktif entegrasyon bulunamadı");
                                  return;
                                }
                                setSourceCreateOpen((v) => !v);
                              }}
                            >
                              {sourceCreateOpen ? "Gizle" : "Yeni Ekle"}
                            </Button>
                          </div>
                          {sourceCreateOpen ? (
                            <div className="space-y-3 border-t px-4 py-3">
                              {String(selectedIntegrationRow?.provider || "").toLowerCase() !== "trendyol" ? (
                                <>
                                  <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
                                    <Input
                                      value={sourceMatchSearch}
                                      onChange={(e) => setSourceMatchSearch(e.target.value)}
                                      placeholder="Template ürün ara"
                                    />
                                    <Button
                                      variant="outline"
                                      onClick={() => fetchSourceMatchProducts(sourceMatchSearch)}
                                      disabled={sourceMatchLoading}
                                    >
                                      {sourceMatchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ara"}
                                    </Button>
                                  </div>

                                  <div className="space-y-1.5">
                                    <Label>Template Ürün</Label>
                                    <Select
                                      value={sourceCreateTemplateExternalId}
                                      onValueChange={setSourceCreateTemplateExternalId}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder={sourceMatchLoading ? "Yükleniyor..." : "Template seçin"} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {sourceMatchItems.map((p) => {
                                          const ext = String(p?.external_id || p?.id || "").trim();
                                          const label = String(p?.name || ext || "Ürün");
                                          return (
                                            <SelectItem key={`create-${ext || label}`} value={ext}>
                                              {label}
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </>
                              ) : null}

                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div className="space-y-1.5">
                                  <Label>Ürün Adı</Label>
                                  <Input
                                    value={sourceCreateForm.product_name}
                                    onChange={(e) => setSourceCreateForm((p) => ({ ...p, product_name: e.target.value }))}
                                    placeholder="Mağazada görünecek ürün adı"
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label>Stok Kodu</Label>
                                  <Input
                                    value={sourceCreateForm.stock_code}
                                    onChange={(e) => setSourceCreateForm((p) => ({ ...p, stock_code: e.target.value }))}
                                    placeholder="SKU / StockCode"
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label>Barkod</Label>
                                  <Input
                                    value={sourceCreateForm.barcode}
                                    onChange={(e) => setSourceCreateForm((p) => ({ ...p, barcode: e.target.value }))}
                                    placeholder="Barkod"
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label>Adet</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={sourceCreateForm.quantity}
                                    onChange={(e) => setSourceCreateForm((p) => ({ ...p, quantity: e.target.value }))}
                                    placeholder="0"
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label>Satış Fiyatı</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={sourceCreateForm.sale_price}
                                    onChange={(e) => setSourceCreateForm((p) => ({ ...p, sale_price: e.target.value }))}
                                    placeholder="0.00"
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label>Liste Fiyatı</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={sourceCreateForm.list_price}
                                    onChange={(e) => setSourceCreateForm((p) => ({ ...p, list_price: e.target.value }))}
                                    placeholder="0.00"
                                  />
                                </div>
                              </div>

                              {String(selectedIntegrationRow?.provider || "").toLowerCase() === "trendyol" ? (
                                <>
                                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                    <div className="space-y-1.5">
                                      <Label>Marka</Label>
                                      <SearchablePicker
                                        value={String(sourceCreateForm.brand_id || "")}
                                        onChange={(val) => setSourceCreateForm((p) => ({ ...p, brand_id: val }))}
                                        options={trendyolBrands}
                                        placeholder="Marka seçin"
                                        searchPlaceholder="Marka ara..."
                                        onSearch={fetchTrendyolBrands}
                                        loading={trendyolBrandLoading}
                                      />
                                    </div>
                                    <div className="space-y-1.5">
                                      <Label>Kategori</Label>
                                      <SearchablePicker
                                        value={String(sourceCreateForm.category_id || "")}
                                        onChange={(val) => setSourceCreateForm((p) => ({ ...p, category_id: val }))}
                                        options={trendyolCategories}
                                        placeholder="Kategori seçin"
                                        searchPlaceholder="Kategori ara..."
                                        onSearch={fetchTrendyolCategories}
                                        loading={trendyolCategoryLoading}
                                      />
                                    </div>
                                    <div className="space-y-1.5">
                                      <Label>Kargo Firması</Label>
                                      <Select
                                        value={String(sourceCreateForm.cargo_company_id || "")}
                                        onValueChange={(val) => setSourceCreateForm((p) => ({ ...p, cargo_company_id: val }))}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder={trendyolMetaLoading ? "Yükleniyor..." : "Kargo firması seçin"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {trendyolCargoCompanies.map((c) => (
                                            <SelectItem key={`cg-${c.id}`} value={String(c.id)}>{c.name}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                    <div className="space-y-1.5">
                                      <Label>Sevkiyat Depo Adresi</Label>
                                      <SearchablePicker
                                        value={String(sourceCreateForm.shipment_address_id || "")}
                                        onChange={(val) => setSourceCreateForm((p) => ({ ...p, shipment_address_id: val }))}
                                        options={trendyolAddresses}
                                        placeholder="Sevkiyat depo seçin"
                                        searchPlaceholder="Depo ara..."
                                        loading={trendyolAddressLoading}
                                        itemLabel={(a) => [a?.name, a?.address_type, a?.district, a?.city].filter(Boolean).join(" · ")}
                                        itemSearch={(a) => [a?.name, a?.address_type, a?.district, a?.city, a?.address].filter(Boolean).join(" ")}
                                      />
                                    </div>
                                    <div className="space-y-1.5">
                                      <Label>İade Depo Adresi</Label>
                                      <SearchablePicker
                                        value={String(sourceCreateForm.returning_address_id || "")}
                                        onChange={(val) => setSourceCreateForm((p) => ({ ...p, returning_address_id: val }))}
                                        options={trendyolAddresses}
                                        placeholder="İade depo seçin"
                                        searchPlaceholder="Depo ara..."
                                        loading={trendyolAddressLoading}
                                        itemLabel={(a) => [a?.name, a?.address_type, a?.district, a?.city].filter(Boolean).join(" · ")}
                                        itemSearch={(a) => [a?.name, a?.address_type, a?.district, a?.city, a?.address].filter(Boolean).join(" ")}
                                      />
                                    </div>
                                    <div className="space-y-1.5">
                                      <Label>Desi</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={sourceCreateForm.dimensional_weight}
                                        onChange={(e) => setSourceCreateForm((p) => ({ ...p, dimensional_weight: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1.5">
                                      <Label>Teslimat Süresi</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={sourceCreateForm.delivery_duration}
                                        onChange={(e) => setSourceCreateForm((p) => ({ ...p, delivery_duration: e.target.value }))}
                                      />
                                    </div>
                                  </div>
                                  {trendyolAttributes.length ? (
                                    <div className="space-y-2 rounded-md border border-border p-3">
                                      <p className="text-xs font-medium text-foreground/80">Kategori Özellikleri</p>
                                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                        {trendyolAttributes.map((attr) => {
                                          const current = (sourceCreateForm.attributes || []).find((x) => Number(x?.attributeId) === Number(attr.id)) || {};
                                          return (
                                            <div key={`attr-${attr.id}`} className="space-y-1">
                                              <Label>{attr.name}{attr.required ? " *" : ""}</Label>
                                              {Array.isArray(attr.values) && attr.values.length ? (
                                                <Select
                                                  value={String(current.attributeValueId || "")}
                                                  onValueChange={(val) => {
                                                    setSourceCreateForm((prev) => {
                                                      const others = (prev.attributes || []).filter((x) => Number(x?.attributeId) !== Number(attr.id));
                                                      return { ...prev, attributes: [...others, { attributeId: attr.id, attributeValueId: Number(val) }] };
                                                    });
                                                  }}
                                                >
                                                  <SelectTrigger>
                                                    <SelectValue placeholder="Seçin" />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {attr.values.map((v) => <SelectItem key={`attrv-${attr.id}-${v.id}`} value={String(v.id)}>{v.name}</SelectItem>)}
                                                  </SelectContent>
                                                </Select>
                                              ) : (
                                                <Input
                                                  value={String(current.customAttributeValue || "")}
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    setSourceCreateForm((prev) => {
                                                      const others = (prev.attributes || []).filter((x) => Number(x?.attributeId) !== Number(attr.id));
                                                      return { ...prev, attributes: [...others, { attributeId: attr.id, customAttributeValue: val }] };
                                                    });
                                                  }}
                                                  placeholder="Değer girin"
                                                />
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ) : null}
                                </>
                              ) : null}

                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setSourceCreateForm({
                                      product_name: String(product?.name || ""),
                                      stock_code: String(product?.sku || ""),
                                      barcode: String(product?.barcode || ""),
                                      sale_price: String(product?.price ?? ""),
                                      list_price: String(product?.regular_price ?? product?.price ?? ""),
                                      quantity: String(product?.stock ?? ""),
                                      category_id: "",
                                      brand_id: "",
                                      cargo_company_id: "",
                                      shipment_address_id: "",
                                      returning_address_id: "",
                                      dimensional_weight: "1",
                                      delivery_duration: "1",
                                      attributes: [],
                                    });
                                  }}
                                >
                                  Ayarları Otomatik Doldur
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setSourceCreateOpen(false);
                                    setSourceMatchSearch("");
                                  }}
                                >
                                  Vazgeç
                                </Button>
                                <Button onClick={handleSourceCreateFromTemplate} disabled={sourceCreating}>
                                  {sourceCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                  Oluştur ve Eşleştir
                                </Button>
                              </div>
                              {sourceCreateResult ? (
                                <div
                                  className={cn(
                                    "rounded-md border px-3 py-2 text-xs",
                                    sourceCreateResult.ok
                                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                      : "border-rose-200 bg-rose-50 text-rose-800"
                                  )}
                                >
                                  <p className="font-medium">{sourceCreateResult.message}</p>
                                  <p className="mt-1">
                                    Kaynak: {sourceCreateResult.template_external_id ? `Template ${sourceCreateResult.template_external_id}` : "Manuel Form"}
                                    {" · "}
                                    Entegrasyon Kimliği: {sourceCreateResult.external_id || "-"}
                                  </p>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  ) : null}

                  {selectedIntegrationRow?.provider === "n11" ? (
                    <>
                      <div className="rounded-lg border border-border/80 bg-card">
                        <div className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ProviderMark provider="n11" ok />
                            <div>
                              <p className="text-base font-semibold">Yeni Ürün Oluştur</p>
                              <p className="text-xs text-muted-foreground">n11 mağazamda ürünü oluşturmak istiyorum</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setN11CreateOpen((v) => !v)}>
                            {n11CreateOpen ? "Gizle" : "Oluştur"}
                          </Button>
                        </div>
                        {n11CreateOpen ? (
                          <div className="border-t px-4 py-3 text-sm text-muted-foreground">
                            Bu adım için N11 yeni ürün oluşturma API akışını ayrıca devreye alacağız.
                            Kategori seçimi ve mevcut ürün eşleştirme tarafı hazır.
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-lg border border-border/80 bg-card">
                        <div className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ProviderMark provider="n11" ok={Boolean(selectedIntegrationRow?.externalId)} />
                            <div>
                              <p className="text-base font-semibold">Mağazadaki Mevcut Ürünü Seç</p>
                              <p className="text-xs text-muted-foreground">n11 mağazamdaki mevcut ürün ile eşleştirmek istiyorum</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setN11MatchOpen((v) => !v)}>
                            {n11MatchOpen ? "Gizle" : "Seç"}
                          </Button>
                        </div>
                        {n11MatchOpen ? (
                          <div className="space-y-3 border-t px-4 py-3">
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
                              <Input
                                value={n11Search}
                                onChange={(e) => setN11Search(e.target.value)}
                                placeholder="Mağaza ürün adı veya stok kodu giriniz"
                              />
                              <Button
                                variant="outline"
                                onClick={() => fetchN11SourceProducts(n11Search)}
                                disabled={n11Loading}
                              >
                                {n11Loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ara"}
                              </Button>
                            </div>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div className="space-y-1.5">
                                <Label>Mağaza Ürünü</Label>
                                <Select value={n11SelectedExternalId} onValueChange={setN11SelectedExternalId}>
                                  <SelectTrigger>
                                    <SelectValue placeholder={n11Loading ? "Yükleniyor..." : "Ürün seçin"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {n11SourceProducts.map((p) => {
                                      const ext = String(p?.external_id || p?.id || "").trim();
                                      return (
                                        <SelectItem key={ext || p?.name} value={ext}>
                                          {p?.name || ext}
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label>N11 Kategorisi</Label>
                                <Select value={n11SelectedCategoryKey} onValueChange={setN11SelectedCategoryKey}>
                                  <SelectTrigger>
                                    <SelectValue placeholder={n11CategoryLoading ? "Kategoriler yükleniyor..." : "Kategori seçin"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {n11Categories.map((c) => {
                                      const key = `${c?.category_id || ""}|${c?.category_name || ""}`;
                                      const label = c?.category_path || c?.category_name || c?.category_id || "Kategori";
                                      return (
                                        <SelectItem key={key || label} value={key}>
                                          {label}
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {n11RecommendedProduct ? (
                              <div className="rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                                Önerilen Mağaza Ürünü: <span className="font-semibold">{n11RecommendedProduct?.name}</span>
                                {" "} / Stok: {n11RecommendedProduct?.sku || "-"} / Barkod: {n11RecommendedProduct?.barcode || "-"}
                              </div>
                            ) : null}

                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setN11MatchOpen(false);
                                  setN11Search("");
                                }}
                              >
                                Vazgeç
                              </Button>
                              <Button onClick={handleN11SaveMatch} disabled={n11Saving}>
                                {n11Saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Kaydet
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </div>
              )}

              {view.section === "job-history" && (
                <div className="space-y-4">
                  <div className="overflow-hidden rounded-lg border border-border/80">
                    <div className="min-w-[760px]">
                      <div className={tableHeader}>
                        <div className="col-span-5">Entegrasyon</div>
                        <div className="col-span-4">Durum</div>
                        <div className="col-span-3 text-right">Son Kontrol Tarihi</div>
                      </div>
                      <div className="max-h-[520px] overflow-auto divide-y">
                      {insightsLoading ? (
                        <div className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>
                      ) : integrationOps.length === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground">Kayıt bulunamadı.</div>
                      ) : integrationOps.map((row, idx) => (
                        <div key={`${row.timestamp}-${idx}`} className="grid grid-cols-12 items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted/40">
                          <div className="col-span-5 flex items-center gap-2">
                            <ProviderMark provider={row.provider} ok={row.status !== "failed"} />
                            <span className="truncate">
                              {row.integration_no ? `#${row.integration_no} ` : ""}{row.integration_name || row.provider_label}
                            </span>
                          </div>
                          <div className="col-span-4">
                            <Badge variant="outline" className={cn(
                              row.status === "success" && "border-emerald-300 bg-emerald-50 text-emerald-700",
                              row.status === "failed" && "border-red-300 bg-red-50 text-red-700",
                              row.status === "processing" && "border-amber-300 bg-amber-50 text-amber-700",
                            )}>
                              {row.status === "failed" ? "Başarısız: Hata Oluştu" : row.status === "success" ? "Başarılı" : "İşleniyor"}
                            </Badge>
                          </div>
                          <div className="col-span-3 text-right text-xs text-muted-foreground">{new Date(row.timestamp).toLocaleString("tr-TR")}</div>
                        </div>
                      ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {view.section === "stock-history" && (
                <div className="space-y-4">
                  <div className="overflow-hidden rounded-lg border border-border/80">
                    <div className="min-w-[760px]">
                      <div className={tableHeader}>
                        <div className="col-span-5">Sipariş / Kaynak</div>
                        <div className="col-span-3">Tarih</div>
                        <div className="col-span-2 text-right">Miktar</div>
                        <div className="col-span-2 text-right">Tür</div>
                      </div>
                      <div className="max-h-[520px] overflow-auto divide-y">
                      {insightsLoading ? (
                        <div className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>
                      ) : timelineMovements.length === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground">Kayıt bulunamadı.</div>
                      ) : timelineMovements.map((row, idx) => (
                        <div key={`${row.timestamp}-${idx}`} className="grid grid-cols-12 items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted/40">
                          <div className="col-span-5 flex items-center gap-2">
                            <ProviderMark provider={row.provider} ok />
                            <span className="truncate">
                              {row.integration_no ? `#${row.integration_no} | ` : ""}{row.integration_name || row.provider_label} | {row.order_no || row.reason}
                            </span>
                          </div>
                          <div className="col-span-3 text-xs text-muted-foreground">{new Date(row.timestamp).toLocaleString("tr-TR")}</div>
                          <div className="col-span-2 text-right tabular-nums">{row.quantity} Adet</div>
                          <div className="col-span-2 text-right">
                            <Badge variant="outline" className={cn(
                              row.movement_type === "in" && "border-sky-300 bg-sky-50 text-sky-700",
                              row.movement_type === "out" && "border-red-300 bg-red-50 text-red-700",
                              row.movement_type === "adjustment" && "border-amber-300 bg-amber-50 text-amber-700",
                            )}>{row.movement_label}</Badge>
                          </div>
                        </div>
                      ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* eslint-disable-next-line react/jsx-no-duplicate-props */}
              {view.section === "sales-reports" && (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <div className="flex items-center gap-1">
                      {[{ key: "day", label: "Gün" }, { key: "week", label: "Hafta" }, { key: "month", label: "Ay" }, { key: "year", label: "Yıl" }].map((r) => (
                        <Button
                          key={r.key}
                          variant={rangeFilter === r.key ? "default" : "outline"}
                          size="sm"
                          className="h-8 px-3 text-xs"
                          onClick={() => setRangeFilter(r.key)}
                        >
                          {r.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                    <div className={cn(panelCard, "xl:col-span-2")}>
                      <div className="flex items-center justify-between px-5 pt-4 pb-2">
                        <h3 className="font-semibold">Finansal Durum ve Sipariş Özeti</h3>
                      </div>
                      <div className="space-y-4 px-5 pb-5">
                        <div className="grid grid-cols-3 gap-2">
                          <div className="rounded-lg border bg-muted/20 p-3">
                            <p className="text-xs text-muted-foreground">Toplam Ciro</p>
                            <p className="mt-1 text-lg font-semibold">{formatCurrency(Number(sales.total_revenue || 0))}</p>
                          </div>
                          <div className="rounded-lg border bg-muted/20 p-3">
                            <p className="text-xs text-muted-foreground">Toplam Sipariş</p>
                            <p className="mt-1 text-lg font-semibold tabular-nums">{sales.total_orders || 0}</p>
                          </div>
                          <div className="rounded-lg border bg-muted/20 p-3">
                            <p className="text-xs text-muted-foreground">Toplam Adet</p>
                            <p className="mt-1 text-lg font-semibold tabular-nums">{sales.total_quantity || 0}</p>
                          </div>
                        </div>

                        <div className="h-[240px] w-full sm:h-[280px]">
                          {chartRows.length === 0 ? (
                            <p className="pt-6 text-sm text-muted-foreground">Seçili aralıkta veri yok.</p>
                          ) : (
                            <ResponsiveContainer width="100%" height="100%">
                              <ReBarChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="shortName" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <ReTooltip />
                                <Bar dataKey="revenue" name="Ciro" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                                <Bar dataKey="orders" name="Sipariş" fill="#94a3b8" radius={[6, 6, 0, 0]} />
                              </ReBarChart>
                            </ResponsiveContainer>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={panelCard}>
                      <div className="flex items-center justify-between px-5 pt-4 pb-2">
                        <h3 className="font-semibold">Mağaza Bazlı Satışlar</h3>
                      </div>
                      <div className="space-y-2 px-5 pb-5">
                        <div className="h-[160px] w-full sm:h-[180px]">
                          {chartRows.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <RePieChart>
                                <Pie data={(sales.by_provider || []).map((r) => ({ name: r.integration_name || r.provider_label, value: Number(r.revenue || 0) }))} dataKey="value" nameKey="name" innerRadius={42} outerRadius={68}>
                                  {(sales.by_provider || []).map((row, i) => (
                                    <Cell key={row.provider} fill={pieColors[i % pieColors.length]} />
                                  ))}
                                </Pie>
                                <ReTooltip />
                              </RePieChart>
                            </ResponsiveContainer>
                          ) : null}
                        </div>
                        {(sales.by_provider || []).slice(0, 8).map((row) => (
                          <div key={row.provider} className="flex items-center justify-between rounded-md border bg-muted/20 px-2.5 py-2 text-sm">
                            <span className="flex items-center gap-2"><ProviderMark provider={row.provider} ok />{row.integration_name || row.provider_label}</span>
                            <span className="font-semibold">{formatCurrency(Number(row.revenue || 0))}</span>
                          </div>
                        ))}
                        {(sales.by_provider || []).length === 0 && <p className="text-sm text-muted-foreground">Kayıt bulunamadı.</p>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="order-1 space-y-4 lg:order-2 lg:col-span-3">
          <div className={panelCard + " overflow-hidden"}>
            <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-[#f7fafc]">
              <p className="text-[9px] font-[650] uppercase tracking-[0.12em] text-muted-foreground">Seçili Ürün</p>
              <Badge variant="secondary">Basit Ürün</Badge>
            </div>
            <div className="space-y-2 p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-muted/50 sm:h-20 sm:w-20">
                  {selectedImage ? (
                    <img src={selectedImage} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      <ImageIcon className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="line-clamp-2 text-sm font-semibold sm:text-base">{product.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{product.sku} | {product.barcode || product.sku}</p>
                </div>
              </div>
            </div>
          </div>

          <div className={panelCard + " overflow-hidden"}>
            <div className="px-4 py-3 border-b border-border bg-[#f7fafc]">
              <p className="text-[9px] font-[650] uppercase tracking-[0.12em] text-muted-foreground">Bölümler</p>
            </div>
            <div className="flex flex-col gap-0.5 p-2">
              {[
                { key: "information", label: "Genel Bilgiler" },
                { key: "images", label: "Resimler" },
              ].map(({ key, label }) => (
                <button key={key} onClick={() => selectMainSection(key)} className={cn(
                  "block w-full rounded-lg px-3 py-2 text-left text-[12px] font-[500] transition-colors border",
                  view.section === key ? "bg-[#ebf5f8] border-[#c8e3ec] text-[#1487a3] font-[650]" : "bg-transparent border-transparent text-muted-foreground hover:bg-muted/50"
                )}>{label}</button>
              ))}
              <p className="px-3 pb-1 pt-2 text-[9px] font-[700] uppercase tracking-[0.1em] text-muted-foreground">Entegrasyon Ürünleri</p>
              {integrationRows.map((row) => (
                <button
                  key={row.provider}
                  onClick={() => selectIntegrationSection(row.provider)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-[12px] border transition-colors",
                    view.section === "integrations" && view.provider === row.provider
                      ? "bg-[#ebf5f8] border-[#c8e3ec] text-[#1487a3] font-[650]"
                      : "bg-transparent border-transparent text-muted-foreground hover:bg-muted/50"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <ProviderMark provider={row.provider} ok={row.matched} />
                    <span>{row.integration?.integration_no ? `#${row.integration.integration_no} ` : ""}{row.label}</span>
                  </span>
                </button>
              ))}
              {[
                { key: "job-history", label: "Entegrasyon İşlemleri" },
                { key: "stock-history", label: "Stok Hareketleri" },
                { key: "sales-reports", label: "Satış Raporları" },
              ].map(({ key, label }) => (
                <button key={key} onClick={() => selectMainSection(key)} className={cn(
                  "block w-full rounded-lg px-3 py-2 text-left text-[12px] font-[500] transition-colors border",
                  view.section === key ? "bg-[#ebf5f8] border-[#c8e3ec] text-[#1487a3] font-[650]" : "bg-transparent border-transparent text-muted-foreground hover:bg-muted/50"
                )}>{label}</button>
              ))}
            </div>
          </div>

          <div className={cn("rounded-xl border-2 shadow-sm", stockBg)}>
            <div className="p-4">
              <div className="mb-2 flex items-center gap-2 text-sky-700">
                <Tag className="h-9 w-9" />
                <div>
                  <p className="text-sm text-muted-foreground">Stok Adedi</p>
                  <p className="text-lg font-semibold">{stockStatus}</p>
                </div>
              </div>
              <p className="mb-3 text-3xl font-bold tabular-nums text-sky-700 sm:text-4xl">{effectiveStockLevel} <span className="text-base font-medium sm:text-lg">Adet</span></p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={openStockSheet}
              >
                Güncelle
              </Button>
              {productSettings?.selected_warehouse_name ? (
                <p className="mt-2 text-xs text-muted-foreground">Depo: {productSettings.selected_warehouse_name}</p>
              ) : null}
            </div>
          </div>

        </div>
      </div>

      <Sheet open={stockSheetOpen} onOpenChange={setStockSheetOpen}>
        <SheetContent className="w-full sm:w-[400px] sm:max-w-[400px]">
          <SheetHeader>
            <SheetTitle>Stok Güncelleme</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div className="space-y-3">
              <Label>Stok Adedi</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={stockAmount}
                  onChange={(e) => setStockAmount(e.target.value)}
                  placeholder="0"
                  className="text-lg font-semibold"
                />
                <span className="whitespace-nowrap text-sm text-muted-foreground">Adet</span>
              </div>
            </div>

            <div className="flex overflow-hidden rounded-lg border">
              {[
                { key: "adjust", label: "Eşitle", icon: Equal },
                { key: "add", label: "Ekle", icon: Plus },
                { key: "subtract", label: "Çıkart", icon: Minus },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setStockMode(m.key)}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors",
                    stockMode === m.key
                      ? m.key === "add"
                        ? "bg-emerald-500 text-white"
                        : m.key === "subtract"
                          ? "bg-red-500 text-white"
                          : "bg-sky-500 text-white"
                      : "hover:bg-muted"
                  )}
                >
                  <m.icon className="h-3.5 w-3.5" />
                  {m.label}
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Açıklama (opsiyonel)</Label>
              <Input value={stockReason} onChange={(e) => setStockReason(e.target.value)} placeholder="Stok güncelleme nedeni..." />
            </div>

            <div className="space-y-2">
              <Label>Hedef Platform</Label>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                {accountingProviderLabel[activeAccountingProvider] || "Muhasebe"}
              </div>
            </div>

            <Button onClick={handleStockUpdate} disabled={stockUpdating || !stockAmount} className="w-full">
              {stockUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Güncelle
            </Button>

            <div>
              <h4 className="mb-3 flex items-center gap-1.5 text-sm font-medium"><History className="h-4 w-4" />Son Stok Hareketleri</h4>
              <div className="max-h-[260px] space-y-2 overflow-y-auto">
                {stockMovements.length === 0 ? (
                  <p className="py-4 text-center text-xs text-muted-foreground">Henüz stok hareketi yok</p>
                ) : stockMovements.map((mov) => (
                  <div key={mov.id} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium">{mov.reason}</p>
                      <p className="text-xs text-muted-foreground">{new Date(mov.created_at).toLocaleString("tr-TR")}</p>
                    </div>
                    <Badge variant="outline" className={cn(
                      mov.movement_type === "in" ? "border-sky-300 bg-sky-50 text-sky-700" :
                      mov.movement_type === "out" ? "border-red-300 bg-red-50 text-red-700" :
                      "border-amber-300 bg-amber-50 text-amber-700"
                    )}>
                      {mov.movement_type === "in" ? "+" : mov.movement_type === "out" ? "-" : "="}{mov.quantity}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
